import type { AppTheme, ResolvedTheme } from "@closer/shared";
import { safeTheme } from "@closer/shared";

/**
 * Tema uygulama katmanı (CLOS-105 `[066]`, ADR-0023).
 *
 * Bileşenler tema BİLMİYOR: tek yaptığımız `<html data-theme>` özniteliğini yazmak;
 * token'lar `packages/ui/src/tokens.css` içinde kendini yeniden tanımlıyor.
 */

/**
 * Çözümlenmiş temanın yerel önbelleği.
 *
 * ⚠ Tercih SUNUCUDA yaşıyor; bu yalnız İLK BOYAMA için bir kopya. Yalnız sunucuya
 * güvenseydik her açılışta açık temadan koyuya bir sıçrama görünürdü — kartın *"belirgin
 * tema sıçraması oluşmaz"* kriterinin ihlali.
 */
export const THEME_STORAGE_KEY = "closer.theme";

const KOYU_SORGU = "(prefers-color-scheme: dark)";

/** İşletim sisteminin tercihi. Tarayıcı dışı ortamda (test/SSR) açık kabul ediliyor. */
export function systemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia(KOYU_SORGU).matches ? "dark" : "light";
}

/** `system` seçeneğini gerçek bir temaya indirger. */
export function resolveTheme(theme: AppTheme): ResolvedTheme {
  return theme === "system" ? systemTheme() : theme;
}

/**
 * Temayı DOM'a yazar ve ilk boyama için önbelleğe alır.
 *
 * Öznitelik, sınıf değil: tema bir DURUM, bir stil değil (ADR-0023).
 */
export function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = resolved;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, resolved);
  } catch {
    // Özel sekmede localStorage yazılamayabilir; tema yine de uygulandı, yalnız bir
    // sonraki açılışta sıçrama olur. Bu, temayı hiç uygulamamaktan iyidir.
  }
}

/**
 * ⚠ Çıkışta ÇAĞRILIYOR. Kartın yedinci kriteri: *"tercih değişikliği başka kullanıcının
 * veya ortak cihazdaki sonraki oturumun bağlamına sızmaz."* Ortak bir bilgisayarda bir
 * sonraki kullanıcı öncekinin temasıyla karşılaşmamalı — tema zararsız görünse de o
 * kişinin hesabından bir bilgidir.
 */
export function clearThemeCache(): void {
  if (typeof document === "undefined") return;
  // ⚠ `applyTheme` DEĞİL: o önbelleğe geri yazardı. Ekran sisteme dönüyor, iz kalmıyor.
  document.documentElement.dataset.theme = systemTheme();
  try {
    localStorage.removeItem(THEME_STORAGE_KEY);
  } catch {
    // Yazılamıyorsa okunamıyordur da; sızacak bir şey yok.
  }
}

/**
 * `system` seçiliyken işletim sistemi değişikliğini İZLER (kartın runtime koşulu).
 *
 * Bir kez okuyup bırakmak yeterli değildi: kullanıcı gün batımında sistemini koyuya
 * alınca uygulama açık kalırdı.
 */
export function watchSystemTheme(onChange: (resolved: ResolvedTheme) => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const mq = window.matchMedia(KOYU_SORGU);
  const dinleyici = (e: MediaQueryListEvent) => onChange(e.matches ? "dark" : "light");
  mq.addEventListener("change", dinleyici);
  return () => mq.removeEventListener("change", dinleyici);
}

/** Sunucudan gelen (ya da bozuk) değeri güvenli bir temaya indirger. */
export function normalizeTheme(value: unknown): AppTheme {
  return safeTheme(value);
}
